import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCurrentUser } from '../store/apiActions';
import { setRbacData } from '../store/actions';
import { tokenManager } from '../utils/utility';
import { RBAC_ENABLED, REQUIRE_AUTH } from '../constant/globalConstants';
import { API_APP_NAME_MAP } from '../constant/rbacConfig';

/**
* Transforms the new current-user format to the old RBAC format structure
* New format: { apps: [{ id, name, role: { access_type: { features: [...] } } }] }
* Old format: { "App Name": { app_id, roles: { "Role Name": { features: {...} } } } }
* 
* @param {Object} currentUserData - The current-user response data
* @returns {Object} Transformed data in the old format
*/
const transformCurrentUserToRbacFormat = (currentUserData) => {
  if (!currentUserData || !currentUserData.apps || !Array.isArray(currentUserData.apps)) {
    return {};
  }

  const transformedData = {};

  currentUserData.apps.forEach((app) => {
    if (!app.is_enabled || !app.name) {
      return;
    }

    const apiAppName = app.name;
    const displayAppName = API_APP_NAME_MAP[apiAppName] || apiAppName;
    const appId = app.id;
    const role = app.role;
    const accessType = role?.access_type;

    if (!role || !accessType) {
      return; // Skip apps without role or access_type
    }

    // Get role name from access_type.type (e.g., "Basic", "Advanced")
    const roleName = accessType.type || role.role || 'Default';

    // Transform features array to object format
    // New: [{ id, key, is_enabled }]
    // Old: { "feature_key": true/false }
    const features = {};
    if (accessType.features && Array.isArray(accessType.features)) {
      accessType.features.forEach((feature) => {
        if (feature.key && feature.is_enabled !== undefined) {
          features[feature.key] = feature.is_enabled;
        }
      });
    }

    // Initialize app data structure with display name as key
    if (!transformedData[displayAppName]) {
      transformedData[displayAppName] = {
        app_id: appId,
        is_enabled: app.is_enabled,
        role_name: role.role,
        roles: {},
        scope: null,
        languages: [],
        countries: [],
      };
    }

    // Add role and features
    transformedData[displayAppName].roles[roleName] = {
      features: features,
    };
  });

  return transformedData;
};

/**
* Custom hook to initialize and fetch RBAC data
* Handles fetching RBAC data on mount if user is authenticated and data is not loaded
* Prevents duplicate API calls
* 
* @param {Object} options - Configuration options
* @param {boolean} options.autoFetch - Whether to automatically fetch on mount (default: true)
* @returns {Object} Object with fetchRbac function and loading state
*/
export const useRbacInit = (options = { autoFetch: true }) => {
  const dispatch = useDispatch();
  const rbacData = useSelector((state) => state.state?.rbacData);
  const fetchingRef = useRef(false);

  const fetchRbac = async () => {
    // Skip fetching if RBAC is disabled globally
    if (!RBAC_ENABLED) {
      return;
    }

    const token = tokenManager.getToken("token");

    // Only fetch if user is authenticated, RBAC data is not in state, and not already fetching
    if (token && !rbacData && !fetchingRef.current) {
      fetchingRef.current = true;
      try {
        const response = await dispatch(fetchCurrentUser());
        if (response && !response.message && typeof response === 'object') {
          const transformedData = transformCurrentUserToRbacFormat(response);
          dispatch(setRbacData(transformedData));
          return transformedData; // Return so callers can use it immediately
        }
      } catch (error) {
        console.error("Error fetching RBAC data:", error);
      } finally {
        fetchingRef.current = false;
      }
    }
  };

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (options.autoFetch) {
      fetchRbac();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  return {
    fetchRbac,
    isRbacLoaded: !RBAC_ENABLED || !REQUIRE_AUTH || !!rbacData,
    isRbacEnabled: RBAC_ENABLED && REQUIRE_AUTH,
  };
};